import { useMemo, useRef, useState } from 'react';
import { Image as KonvaImage, Layer, Rect, Stage, Text } from 'react-konva';
import useImage from 'use-image';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Slider from '@mui/material/Slider';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import type { CertificateFont } from '../../services/certificateFontService';
import { getCertificateFontFamily, useCertificateFontFaces } from './useCertificateFontFaces';
import {
  CERTIFICATE_FIELD_CATEGORY_LABELS,
  CERTIFICATE_FIELD_DEFS,
  getCertificateFieldLabel,
  getCertificateFieldSampleText,
  type CertificateFieldAlign,
  type CertificateFieldCategory,
  type CertificateFieldKey,
  type CertificateTemplateField,
} from '../../services/certificateTemplateService';

const MAX_STAGE_WIDTH = 720;
// 글자 크기는 pt 단위로 저장하고, 화면·서버 모두 96dpi 기준(1pt = 4/3px)으로 px에 환산해 그린다.
const PT_TO_PX = 4 / 3;
const DEFAULT_FONT_SIZE = 24;
const MIN_FONT_SIZE = 7.5;
const MAX_FONT_SIZE = 150;
const FONT_SIZE_STEP = 0.25;
const DEFAULT_WIDTH_PERCENT = 40;
const FIELD_CATEGORIES: CertificateFieldCategory[] = ['EVENT', 'PARTICIPANT', 'RECORD'];

function createDefaultField(
  key: CertificateFieldKey,
  imageHeight: number,
  fontId: number | null
): CertificateTemplateField {
  const heightPercent = imageHeight > 0 ? ((DEFAULT_FONT_SIZE * PT_TO_PX * 1.4) / imageHeight) * 100 : 0;
  return {
    key,
    xPercent: 50 - DEFAULT_WIDTH_PERCENT / 2,
    yPercent: 50 - heightPercent / 2,
    widthPercent: DEFAULT_WIDTH_PERCENT,
    fontSize: DEFAULT_FONT_SIZE,
    color: '#111827',
    align: 'CENTER',
    bold: true,
    fontId,
  };
}

// 서버(CertificateImageRenderer)의 기본 폰트와 같은 의미로, fontId가 없을 때 쓰는 선택지 값
const DEFAULT_FONT_OPTION = 'default';

function RemoveIcon() {
  return (
    <svg aria-hidden="true" fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="14">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

type CertificateTemplateEditorProps = Readonly<{
  imageSrc: string;
  imageWidth: number;
  imageHeight: number;
  fields: CertificateTemplateField[];
  /** 워크벤치에 등록된 폰트 목록. 필드별 폰트 선택지로 쓴다. */
  fonts: CertificateFont[];
  onFieldsChange: (fields: CertificateTemplateField[]) => void;
  onFieldRemoved?: (message: string) => void;
}>;

function CertificateTemplateEditor({
  imageSrc,
  imageWidth,
  imageHeight,
  fields,
  fonts,
  onFieldsChange,
  onFieldRemoved,
}: CertificateTemplateEditorProps) {
  const [image] = useImage(imageSrc);
  const loadedFontIds = useCertificateFontFaces(fonts);
  const [selectedKey, setSelectedKey] = useState<CertificateFieldKey | null>(null);
  const [showBoxes, setShowBoxes] = useState(true);
  // 새 필드의 기본 폰트. 처음에는 기본 폰트(나눔고딕)이고, 폰트를 지정할 때마다 최근 사용한 폰트로 바뀐다.
  // 편집 화면에 들어올 때마다 초기화된다.
  const [lastUsedFontId, setLastUsedFontId] = useState<number | null>(null);
  // 드래그 중인 텍스트의 현재 좌표. 점선 박스가 텍스트를 실시간으로 따라가도록 쓰고, 드롭하면 비운다.
  const [dragPosition, setDragPosition] = useState<{ key: CertificateFieldKey; x: number; y: number } | null>(null);
  // 드래그를 시작한 위치. Shift 직선 이동의 기준점이다.
  const dragOriginRef = useRef<{ x: number; y: number } | null>(null);

  const scale = useMemo(
    () => Math.min(1, MAX_STAGE_WIDTH / imageWidth),
    [imageWidth]
  );
  const stageWidth = imageWidth * scale;
  const stageHeight = imageHeight * scale;

  const placedKeys = new Set(fields.map((field) => field.key));
  const selectedField = fields.find((field) => field.key === selectedKey) ?? null;

  function updateField(key: CertificateFieldKey, patch: Partial<CertificateTemplateField>) {
    onFieldsChange(fields.map((field) => (field.key === key ? { ...field, ...patch } : field)));
  }

  // 선택한 필드의 폰트를 모든 필드에 똑같이 적용한다 (null이면 전체를 기본 폰트로 되돌린다)
  function applyFontToAll(fontId: number | null) {
    onFieldsChange(fields.map((field) => ({ ...field, fontId })));
    setLastUsedFontId(fontId);
  }

  function changeFont(key: CertificateFieldKey, fontId: number | null) {
    updateField(key, { fontId });
    setLastUsedFontId(fontId);
  }

  function addField(key: CertificateFieldKey) {
    const next = [...fields, createDefaultField(key, imageHeight, lastUsedFontId)];
    onFieldsChange(next);
    setSelectedKey(key);
  }

  function removeField(key: CertificateFieldKey) {
    onFieldsChange(fields.filter((field) => field.key !== key));
    if (selectedKey === key) {
      setSelectedKey(null);
    }
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
      <Box
        sx={{
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: 1,
          overflow: 'auto',
          p: 2,
        }}
      >
        {imageWidth > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <ToggleButton
              onChange={() => setShowBoxes((current) => !current)}
              selected={showBoxes}
              size="small"
              value="showBoxes"
            >
              박스 점선 표시
            </ToggleButton>
          </Box>
        )}
        {imageWidth === 0 ? (
          <Typography sx={{ color: '#64748b', fontSize: 13 }}>이미지를 업로드해 주세요.</Typography>
        ) : (
          <Stage
            width={stageWidth}
            height={stageHeight}
            onMouseDown={(event) => {
              if (event.target === event.target.getStage()) {
                setSelectedKey(null);
              }
            }}
          >
            <Layer>
              {image && <KonvaImage image={image} width={stageWidth} height={stageHeight} />}
              {showBoxes &&
                fields.map((field) => {
                  const boxWidth = (field.widthPercent / 100) * stageWidth;
                  const boxHeight = field.fontSize * PT_TO_PX * scale * 1.4;
                  const isDragging = dragPosition?.key === field.key;
                  const x = isDragging ? dragPosition.x : (field.xPercent / 100) * stageWidth;
                  const y = isDragging ? dragPosition.y : (field.yPercent / 100) * stageHeight;
                  const isSelected = field.key === selectedKey;

                  return (
                    <Rect
                      key={`${field.key}-box`}
                      x={x}
                      y={y}
                      width={boxWidth}
                      height={boxHeight}
                      stroke={isSelected ? '#2563eb' : '#94a3b8'}
                      dash={[4, 4]}
                      strokeWidth={isSelected ? 1.5 : 1}
                      listening={false}
                    />
                  );
                })}
              {fields.map((field) => {
                const boxWidth = (field.widthPercent / 100) * stageWidth;
                const boxHeight = field.fontSize * PT_TO_PX * scale * 1.4;
                const x = (field.xPercent / 100) * stageWidth;
                const y = (field.yPercent / 100) * stageHeight;
                // 사용자 폰트는 로드가 끝난 뒤에만 적용하고, 서버처럼 bold는 무시한다.
                const useCustomFont = field.fontId !== null && loadedFontIds.has(field.fontId);

                return (
                  <Text
                    // 폰트 로드가 끝나면 텍스트 측정값이 바뀌므로 다시 그리도록 key에 반영한다
                    key={`${field.key}-${useCustomFont ? 'custom' : 'default'}`}
                    x={x}
                    y={y}
                    width={boxWidth}
                    height={boxHeight}
                    verticalAlign="middle"
                    text={getCertificateFieldSampleText(field.key)}
                    fontSize={field.fontSize * PT_TO_PX * scale}
                    fontFamily={useCustomFont ? getCertificateFontFamily(field.fontId as number) : undefined}
                    fontStyle={!useCustomFont && field.bold ? 'bold' : 'normal'}
                    fill={field.color}
                    align={field.align.toLowerCase()}
                    draggable
                    onClick={() => setSelectedKey(field.key)}
                    onTap={() => setSelectedKey(field.key)}
                    onDragStart={(event) => {
                      setSelectedKey(field.key);
                      dragOriginRef.current = { x: event.target.x(), y: event.target.y() };
                    }}
                    onDragMove={(event) => {
                      const node = event.target;
                      const origin = dragOriginRef.current;
                      // Shift를 누른 채 드래그하면 더 많이 움직인 축으로만 이동한다 (수평/수직 직선 이동)
                      if (event.evt?.shiftKey && origin) {
                        if (Math.abs(node.x() - origin.x) >= Math.abs(node.y() - origin.y)) {
                          node.y(origin.y);
                        } else {
                          node.x(origin.x);
                        }
                      }
                      setDragPosition({ key: field.key, x: node.x(), y: node.y() });
                    }}
                    onDragEnd={(event) => {
                      setDragPosition(null);
                      const node = event.target;
                      const xPercent = (node.x() / stageWidth) * 100;
                      const yPercent = (node.y() / stageHeight) * 100;
                      if (xPercent < 0 || xPercent > 100 || yPercent < 0 || yPercent > 100) {
                        removeField(field.key);
                        onFieldRemoved?.(`${getCertificateFieldLabel(field.key)} 박스가 이미지 밖으로 나가 삭제되었습니다.`);
                        return;
                      }
                      updateField(field.key, { xPercent, yPercent });
                    }}
                  />
                );
              })}
            </Layer>
          </Stage>
        )}
      </Box>

      <Box sx={{ display: 'grid', flex: '1 1 320px', gap: 2, minWidth: 320 }}>
        <Box>
          <Typography sx={{ color: '#334155', fontSize: 13, fontWeight: 800, mb: 1 }}>
            표시할 필드
          </Typography>
          <Box sx={{ display: 'grid', gap: 1.5, maxHeight: 560, overflowY: 'auto', pr: 0.5 }}>
            {FIELD_CATEGORIES.map((category) => (
              <Box key={category}>
                <Typography sx={{ color: '#94a3b8', fontSize: 11, fontWeight: 800, letterSpacing: 0.3, mb: 0.5, textTransform: 'uppercase' }}>
                  {CERTIFICATE_FIELD_CATEGORY_LABELS[category]}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gap: 0.75,
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                  }}
                >
                  {CERTIFICATE_FIELD_DEFS.filter((def) => def.category === category).map((def) => {
                    const placed = placedKeys.has(def.key);

                    return (
                      <Box
                        key={def.key}
                        onClick={() => placed && setSelectedKey(def.key)}
                        sx={{
                          alignItems: 'center',
                          backgroundColor: def.key === selectedKey ? '#eff6ff' : '#fff',
                          border: '1px solid',
                          borderColor: def.key === selectedKey ? '#93c5fd' : '#e2e8f0',
                          borderRadius: 1,
                          cursor: placed ? 'pointer' : 'default',
                          display: 'flex',
                          justifyContent: 'space-between',
                          px: 1.5,
                          py: 1,
                        }}
                      >
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{def.label}</Typography>
                        {placed ? (
                          <IconButton
                            aria-label={`${def.label} 제거`}
                            onClick={(event) => {
                              event.stopPropagation();
                              removeField(def.key);
                            }}
                            size="small"
                          >
                            <RemoveIcon />
                          </IconButton>
                        ) : (
                          <Button
                            disabled={imageWidth === 0}
                            onClick={() => addField(def.key)}
                            size="small"
                            variant="outlined"
                          >
                            추가
                          </Button>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {selectedField && (
          <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, p: 2 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 800, mb: 1.5 }}>
              {getCertificateFieldLabel(selectedField.key)} 속성
            </Typography>

            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                helperText={
                  selectedField.fontId === null
                    ? fonts.length === 0
                      ? '등록된 폰트가 없습니다. 목록 화면의 폰트 관리에서 추가해 주세요.'
                      : undefined
                    : '사용자 폰트는 굵게(B)가 적용되지 않습니다.'
                }
                label="폰트"
                onChange={(event) =>
                  changeFont(
                    selectedField.key,
                    event.target.value === DEFAULT_FONT_OPTION ? null : Number(event.target.value)
                  )
                }
                select
                size="small"
                value={selectedField.fontId === null ? DEFAULT_FONT_OPTION : String(selectedField.fontId)}
              >
                <MenuItem value={DEFAULT_FONT_OPTION}>기본 (나눔고딕)</MenuItem>
                {fonts.map((font) => (
                  <MenuItem key={font.id} value={String(font.id)}>
                    {font.name}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                disabled={fields.every((field) => field.fontId === selectedField.fontId)}
                onClick={() => applyFontToAll(selectedField.fontId)}
                size="small"
                sx={{ mt: 1 }}
                variant="outlined"
              >
                모든 필드에 폰트 일괄 적용
              </Button>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography sx={{ color: '#64748b', fontSize: 12, mb: 0.5 }}>
                글자 크기 ({selectedField.fontSize}pt)
              </Typography>
              <Slider
                max={MAX_FONT_SIZE}
                min={MIN_FONT_SIZE}
                step={FONT_SIZE_STEP}
                onChange={(_, value) =>
                  updateField(selectedField.key, { fontSize: value as number })
                }
                size="small"
                value={selectedField.fontSize}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography sx={{ color: '#64748b', fontSize: 12, mb: 0.5 }}>
                박스 너비 ({Math.round(selectedField.widthPercent)}%)
              </Typography>
              <Slider
                max={100}
                min={10}
                onChange={(_, value) =>
                  updateField(selectedField.key, { widthPercent: value as number })
                }
                size="small"
                value={selectedField.widthPercent}
              />
            </Box>

            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5, mb: 2 }}>
              <Typography sx={{ color: '#64748b', fontSize: 12 }}>색상</Typography>
              <input
                onChange={(event) =>
                  updateField(selectedField.key, { color: event.target.value })
                }
                style={{ border: 'none', height: 28, padding: 0, width: 40 }}
                type="color"
                value={selectedField.color}
              />
              <ToggleButton
                disabled={selectedField.fontId !== null}
                onChange={() =>
                  updateField(selectedField.key, { bold: !selectedField.bold })
                }
                selected={selectedField.bold}
                size="small"
                sx={{ fontWeight: 800, ml: 'auto' }}
                value="bold"
              >
                B
              </ToggleButton>
            </Box>

            <Box>
              <Typography sx={{ color: '#64748b', fontSize: 12, mb: 0.5 }}>정렬</Typography>
              <ToggleButtonGroup
                exclusive
                onChange={(_, value: CertificateFieldAlign | null) =>
                  value && updateField(selectedField.key, { align: value })
                }
                size="small"
                value={selectedField.align}
              >
                <ToggleButton value="LEFT">왼쪽</ToggleButton>
                <ToggleButton value="CENTER">가운데</ToggleButton>
                <ToggleButton value="RIGHT">오른쪽</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default CertificateTemplateEditor;
